// src/components/dashboard/wall/ResultTableComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT, RAW_REZA_BASE } from "../../../../../lib/api-url";
import useIndividualStream from "../../../../shared/hooks/useIndividualSocket";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";
const BASE_URL = RAW_REZA_BASE; // use your base + "/" + avatar path

const livePointsForRank = (rank) => Math.max(0, 60 - Number(rank) * 5);

function getLoginNumber(row) {
  const cands = [row?.login, row?.id, row?.username, row?.account_login];
  for (const c of cands) {
    if (c == null) continue;
    const n = Number(String(c).trim());
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

const makeSocketUrl = (base, path = "") => {
  if (!base) return "";
  const hasProto = /^(https?:|wss?:)/i.test(base);
  const origin = hasProto ? base : `http://${base}`;
  return `${origin}${path}`;
};

function ensureObject(maybe) {
  if (!maybe) return null;
  if (typeof maybe === "string") {
    try {
      return JSON.parse(maybe);
    } catch {
      return null;
    }
  }
  return maybe;
}

function parseRiskEvent(frame) {
  const obj = ensureObject(frame) || frame;
  const re = obj?.RiskEvent || obj || {};
  const login = Number(re?.login);
  const equity = Number(re?.new_value);
  let balance = Number(re?.corrected_value);
  if (!Number.isFinite(balance)) {
    const m = String(re?.message || "").match(/Balance=\$([0-9.\-]+)/i);
    if (m) balance = Number(m[1]);
  }
  const isFailedRaw =
    re?.is_account_failed ?? re?.risk_state?.is_account_failed;
  const disabled =
    typeof isFailedRaw === "string"
      ? isFailedRaw.toLowerCase() === "true"
      : Boolean(isFailedRaw);
  return {
    login: Number.isFinite(login) ? login : null,
    equity: Number.isFinite(equity) ? equity : null,
    balance: Number.isFinite(balance) ? balance : null,
    disabled,
    ts: re?.timestamp || Date.now(),
  };
}

export default function ResultTableComponent() {
  const [accountsByLogin, setAccountsByLogin] = useState({});
  const [finalists, setFinalists] = useState([]);
  const prevRanksRef = useRef(new Map());
  const hasMountedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const fin = await axios.get(API_ENDPOINT.users.getFinalistsData);
        const items = fin?.data?.items ?? [];
        setFinalists(items);
        const seeded = {};
        for (const it of items) {
          const login = Number(it?.account_login);
          if (!Number.isFinite(login)) continue;
          const eq = Number(it?.equity ?? it?.stage_3_score);
          const bal = Number(it?.balance);
          if (Number.isFinite(eq) || Number.isFinite(bal)) {
            seeded[login] = {
              equity: Number.isFinite(eq) ? eq : null,
              balance: Number.isFinite(bal) ? bal : null,
              disabled: false,
            };
          }
        }
        if (Object.keys(seeded).length) {
          setAccountsByLogin((prev) => ({ ...seeded, ...prev }));
        }
      } catch (e) {
        console.error("ResultTable fetch error:", e);
      }
    })();
  }, []);

  const finalistsByLogin = useMemo(() => {
    const m = new Map();
    for (const it of finalists) {
      const login = Number(it?.account_login);
      if (!Number.isNaN(login)) m.set(login, it);
    }
    return m;
  }, [finalists]);

  const rosterLogins = useMemo(
    () =>
      (Array.isArray(finalists) ? finalists : [])
        .map((f) => Number(f?.account_login))
        .filter((n) => !Number.isNaN(n)),
    [finalists]
  );

  const INDIVIDUAL_ORIGIN = process.env.REACT_APP_INDIVIDUAL_SOCKET;
  const individualUrl = makeSocketUrl(INDIVIDUAL_ORIGIN);

  const { lastItem } = useIndividualStream({
    url: individualUrl,
    token:
      (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
      "",
    enabled: true,
  });

  useEffect(() => {
    if (!lastItem) return;
    const evt = parseRiskEvent(lastItem);
    if (!evt?.login) return;
    setAccountsByLogin((prev) => {
      const old = prev[evt.login] || {};
      return {
        ...prev,
        [evt.login]: {
          equity: evt.equity ?? old.equity ?? null,
          balance: evt.balance ?? old.balance ?? null,
          disabled: evt.disabled ?? old.disabled ?? false,
        },
      };
    });
  }, [lastItem]);

  const accounts = useMemo(() => {
    const arr = [];
    for (const [loginStr, rec] of Object.entries(accountsByLogin)) {
      const login = Number(loginStr);
      if (!Number.isFinite(login)) continue;
      arr.push({
        login,
        equity: Number(rec?.equity ?? 0),
        balance: Number(rec?.balance ?? 0),
        disabled: !!rec?.disabled,
      });
    }
    return arr;
  }, [accountsByLogin]);

  const combined = useMemo(() => {
    if (!rosterLogins.length) return [];
    const liveRows = (Array.isArray(accounts) ? accounts : [])
      .map((row) => {
        const login = getLoginNumber(row);
        return {
          login,
          equity: Number(row?.equity ?? 0),
          balance: Number(row?.balance ?? 0),
          disabled: !!row?.disabled,
        };
      })
      .filter((r) => r.login != null && rosterLogins.includes(r.login));
    const enabledOnly = liveRows.filter((r) => !r.disabled);
    enabledOnly.sort((a, b) => b.balance - a.balance);
    const liveByLogin = new Map();
    enabledOnly.forEach((r, i) => {
      const rtRank = i + 1;
      liveByLogin.set(r.login, {
        equity: r.equity,
        balance: r.balance,
        disabled: false,
        rtRank,
        rtPoints: livePointsForRank(rtRank),
      });
    });
    liveRows
      .filter((r) => r.disabled)
      .forEach((r) => {
        liveByLogin.set(r.login, {
          equity: r.equity,
          balance: r.balance,
          disabled: true,
          rtRank: null,
          rtPoints: 0,
        });
      });
    const rows = rosterLogins.map((login) => {
      const fin = finalistsByLogin.get(login);
      const live = liveByLogin.get(login);
      const fullName = `${fin?.first_name ?? ""} ${fin?.last_name ?? ""}`.trim();
      const displayName = fullName || fin?.username || String(login);
      const apiPoints = Number(fin?.points ?? 0) || 0;
      const disabled = !!live?.disabled;
      const rtRank = disabled ? null : live?.rtRank ?? null;
      const rtPoints = disabled ? 0 : live?.rtPoints ?? 0;
      const equity = Number.isFinite(Number(live?.equity))
        ? Number(live?.equity)
        : Number(fin?.stage_3_score ?? 0);
      const balance = Number(live?.balance ?? 0);
      const totalPoints = apiPoints + rtPoints;

      // 🔹 Implement avatar: BASE_URL + "/" + avatar path (or default)
      const profileImage = fin?.avatar
        ? `${BASE_URL}/${fin.avatar}`
        : DEFAULT_AVATAR;

      return {
        id: login,
        login,
        name: displayName,
        equity,
        balance,
        rtRank,
        rtPoints,
        apiPoints,
        totalPoints,
        disabled,
        profileImage,
      };
    });
    rows.sort((a, b) => {
      if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
      if (b.balance !== a.balance) return b.balance - a.balance;
      if ((a.rtRank ?? 9999) !== (b.rtRank ?? 9999))
        return (a.rtRank ?? 9999) - (b.rtRank ?? 9999);
      return a.login - b.login;
    });
    return rows.map((u, i) => ({
      ...u,
      rank: u.disabled ? null : i + 1,
    }));
  }, [accounts, rosterLogins, finalistsByLogin]);

  const swappedIds = useMemo(() => {
    const prev = prevRanksRef.current;
    const changed = new Set();
    for (const u of combined) {
      if (u.disabled) continue;
      const oldRank = prev.get(u.id);
      if (oldRank != null && oldRank !== u.rank) changed.add(u.id);
    }
    const next = new Map(
      combined.filter((u) => !u.disabled).map((u) => [u.id, u.rank])
    );
    prevRanksRef.current = next;
    return changed;
  }, [combined]);

  const top20 = combined.slice(0, 20);
  const firstTen = top20.slice(0, 10);
  const nextTen = top20.slice(10, 20);

  return (
    <div style={{ width: "100%" }}>
      <style>{css}</style>
      <div className="tables-container w-full flex items-center justify-center">
        <div className="flex flex-row-reverse gap-[150px] w-[1085px] min-h-[703px]">
          <div className="list-scroll p-8 flex flex-col items-center justify-start gap-1 relative">
            <HeaderPlaque />
            {firstTen.map((user, index) => (
              <RowCard
                key={user.id}
                user={user}
                animationDelay={index * 80}
                swapped={swappedIds.has(user.id)}
                hasMountedRef={hasMountedRef}
              />
            ))}
          </div>
          <div className="list-scroll p-8 flex flex-col items-center justify-start gap-1 relative">
            <HeaderPlaque />
            {nextTen.map((user, index) => (
              <RowCard
                key={user.id}
                user={user}
                animationDelay={index * 80}
                swapped={swappedIds.has(user.id)}
                hasMountedRef={hasMountedRef}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderPlaque() {
  return (
    <div className="table-header-container absolute top-[12px] left-[40px] w-[281px] flex justify-center items-center gap-[33px]">
      <img
        src="/container/table-header-container.svg"
        className="w-[281px] absolute z-[-1]"
        alt=""
      />
      <div className="text-container flex justify-center items-center gap-[33px] relative z-10">
        <span className="text-[#FFFFFF] text-[10px] font-bold">امتیاز</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">اکوییتی</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">بالانس</span>
        <span className="text-[#FFFFFF] text-[10px] font-bold">رتبه</span>
      </div>
    </div>
  );
}

function RowCard({ user, animationDelay = 0, swapped = false, hasMountedRef }) {
  const mounted = hasMountedRef.current;
  useEffect(() => {
    hasMountedRef.current = true;
  }, [hasMountedRef]);

  const initialAnim = mounted ? false : { opacity: 0, x: -30 };
  const bannerSrc = user.disabled
    ? "/expired/expired-player-banner.svg"
    : "/container/player-box-container.png";

  return (
    <motion.div
      className="relative new-row"
      initial={initialAnim}
      animate={{ opacity: 1, x: 0, scale: swapped ? [1, 1.03, 1] : 1 }}
      transition={{
        opacity: { duration: mounted ? 0 : 0.42, delay: animationDelay / 1000 },
        x: { duration: mounted ? 0 : 0.42, delay: animationDelay / 1000 },
        scale: swapped
          ? { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }
          : undefined,
      }}
      layout
      layoutDependency={user.rank ?? -1}
      layoutTransition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <img className="w-[436px]" src={bannerSrc} alt="" />
      <img
        src={user.profileImage || DEFAULT_AVATAR}
        className="w-[43px] absolute right-[20px] bottom-[28px] z-10"
        alt=""
      />
      <div className="absolute top-[-10px] left-0 w-full h-full flex items-center justify-start gap-[24px] flex-row-reverse">
        <span className="text-[#FFFFFF] text-[8px] font-bold mr-[90px] w-[120px] truncate">
          {user.name}
        </span>
        <div className="w-full flex justify-end items-center gap-[30px]">
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.totalPoints)}
          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.equity)}
          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {fmt(user.balance)}

          </span>
          <span className="text-[#FFFFFF] text-[10px] font-bold">
            {user.rank ?? "-"}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "-";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

const css = `
.list-scroll::-webkit-scrollbar { width: 0; height: 0; }
.list-scroll {
  width: 100%;
  overflow: hidden;
  padding-right: 8px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.table-header-container { pointer-events: none; }
.new-row { width: 436px; height: 66px; }
`;
